import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Spinner } from 'react-bootstrap';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Avatar } from 'primereact/avatar';
import axios from 'axios';
import '../assets/css/unified-task-details.css';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import noUserImg from '../assets/images/no_user.png';

const formatDateTime = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getPriorityBadgeClass = (priority) => {
    switch (priority) {
        case 'Urgent':
        case 'High':
            return 'bg-danger';
        case 'Medium':
            return 'bg-warning text-dark';
        case 'Low':
            return 'bg-info';
        default:
            return 'bg-secondary';
    }
};

const isCompletedStatus = (status) => {
    const s = String(status || '').toLowerCase().replace(/[\s_-]+/g, '');
    return s === 'completed' || s === 'done';
};

const formatPersonName = (person, userId = null, usersById = {}) => {
    if (person?.first_name || person?.last_name) {
        return `${person.first_name || ''} ${person.last_name || ''}`.trim();
    }
    const id = person?.user_id ?? userId;
    if (id != null && usersById[id]) {
        const u = usersById[id];
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
        if (name) return name;
    }
    if (id != null) return `User #${id}`;
    return 'Unknown';
};

const UnifiedTaskDetails = ({ taskId, cardId, onClose, compact = false, onReviewAction }) => {
    const { accessToken, userId } = useAuth();
    const isCard = Boolean(cardId);
    const entityId = cardId || taskId;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [task, setTask] = useState(null);
    const [comments, setComments] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [activity, setActivity] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [reviewUsers, setReviewUsers] = useState([]);
    const [usersById, setUsersById] = useState({});
    const [selectedReviewerId, setSelectedReviewerId] = useState(null);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);

    const headers = { Authorization: `Bearer ${accessToken}` };

    const reviewApiBase = isCard
        ? `${config.apiBASEURL}/TaskBoardRoutes/cards/${cardId}`
        : `${config.apiBASEURL}/projectRoutes/tasks/${taskId}`;

    const usersApiUrl = isCard
        ? `${config.apiBASEURL}/TaskBoardRoutes/fetch-all-users`
        : `${config.apiBASEURL}/projectRoutes/fetch-all-users`;

    const loadTaskDetails = useCallback(async () => {
        if (!entityId || !accessToken) return;
        setLoading(true);
        setError('');
        try {
            const url = isCard
                ? `${config.apiBASEURL}/TaskBoardRoutes/cards/${cardId}`
                : `${config.apiBASEURL}/projectRoutes/tasks/${taskId}`;
            const response = await axios.get(url, { headers });
            setTask(response.data.task);
            setComments(response.data.includes?.comments || []);
            setAttachments(response.data.includes?.attachments || []);
            setActivity(response.data.includes?.activity || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load task details');
        } finally {
            setLoading(false);
        }
    }, [entityId, taskId, cardId, accessToken, isCard]);

    useEffect(() => {
        loadTaskDetails();
    }, [loadTaskDetails]);

    useEffect(() => {
        if (!accessToken) return;
        const loadUsers = async () => {
            try {
                const response = await axios.get(usersApiUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
                const lookup = {};
                const options = (response.data || []).map((u) => {
                    lookup[u.user_id] = u;
                    return {
                        label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || `User #${u.user_id}`,
                        value: u.user_id,
                    };
                });
                setUsersById(lookup);
                setReviewUsers(options);
            } catch {
                setReviewUsers([]);
            }
        };
        loadUsers();
    }, [accessToken, usersApiUrl]);

    useEffect(() => {
        if (task?.reviewer_user_id && task?.review_status === 'changes_requested') {
            setSelectedReviewerId(task.reviewer_user_id);
        }
    }, [task?.reviewer_user_id, task?.review_status]);

    const afterReviewAction = async () => {
        await loadTaskDetails();
        onReviewAction?.();
    };

    const handleAddComment = async () => {
        if (isCard) return;
        const body = newComment.trim();
        if (!body) return;
        setSubmitting(true);
        try {
            await axios.post(
                `${config.apiBASEURL}/projectRoutes/tasks/${taskId}/comments`,
                { body, user_id: userId },
                { headers }
            );
            setNewComment('');
            await loadTaskDetails();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add comment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUploadAttachment = async (event) => {
        if (isCard || !taskId) return;
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('user_id', userId);

            await axios.post(
                `${config.apiBASEURL}/projectRoutes/tasks/${taskId}/attachments`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            event.target.value = '';
            await loadTaskDetails();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload attachment');
        } finally {
            setUploading(false);
        }
    };

    const handleRequestReview = async () => {
        if (!entityId || !selectedReviewerId) return;
        setReviewSubmitting(true);
        setError('');
        try {
            const response = await axios.post(
                `${reviewApiBase}/request-review`,
                {
                    reviewer_user_id: selectedReviewerId || task.reviewer_user_id,
                    user_id: userId,
                },
                { headers }
            );
            if (response.data?.warning) {
                setError(response.data.warning);
            }
            await afterReviewAction();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to request review');
        } finally {
            setReviewSubmitting(false);
        }
    };

    const handleApproveReview = async () => {
        if (!entityId) return;
        setReviewSubmitting(true);
        setError('');
        try {
            await axios.post(
                `${reviewApiBase}/approve-review`,
                { comment: reviewComment, user_id: userId },
                { headers }
            );
            setReviewComment('');
            await afterReviewAction();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to approve review');
        } finally {
            setReviewSubmitting(false);
        }
    };

    const handleRequestChanges = async () => {
        if (!entityId) return;
        setReviewSubmitting(true);
        setError('');
        try {
            await axios.post(
                `${reviewApiBase}/request-changes`,
                { comment: reviewComment, user_id: userId },
                { headers }
            );
            setReviewComment('');
            await afterReviewAction();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to request changes');
        } finally {
            setReviewSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading task details...
            </div>
        );
    }

    if (error && !task) {
        return <div className="alert alert-danger m-3">{error}</div>;
    }

    if (!task) {
        return <div className="alert alert-warning m-3">Task not found.</div>;
    }

    const assigneeName = task.assignee
        ? formatPersonName(task.assignee, task.assignee?.user_id, usersById)
        : 'Unassigned';

    const reviewerName = formatPersonName(
        task.reviewer,
        task.reviewer_user_id,
        usersById
    );

    const resolveCommentAuthorName = (comment) =>
        formatPersonName(comment.author, comment.author?.user_id, usersById);

    const resolveActivityActorName = (item) =>
        formatPersonName(item.actor, item.actor?.user_id, usersById);

    const isSameUser = (a, b) => a != null && b != null && String(a) === String(b);

    const isPendingReview = isCard
        ? task.review_status === 'pending'
        : task.status === 'InReview';

    const isAssignedReviewer =
        isSameUser(task.reviewer_user_id, userId) &&
        isPendingReview &&
        (task.review_status === 'pending' || task.review_status === 'none' || !task.review_status);

    const canRequestReview = isCard
        ? !isPendingReview &&
          !isCompletedStatus(task.status) &&
          ['none', 'changes_requested', 'approved', null, undefined].includes(task.review_status)
        : task.status !== 'Completed' &&
          !isAssignedReviewer &&
          ['InProgress', 'InChanges'].includes(task.status);

    return (
        <div className={`unified-task-details p-2${compact ? ' unified-task-details--compact' : ''}`}>
            {!compact && (
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h5 className="mb-1">{task.title}</h5>
                        <small className="text-muted">
                            {isCard ? 'Board card' : 'ERP task'}
                            {task.project?.project_name ? ` · ${task.project.project_name}` : ''}
                            {task.project?.brand?.brand_name ? ` · ${task.project.brand.brand_name}` : ''}
                        </small>
                    </div>
                    {onClose && (
                        <Button icon="pi pi-times" rounded text severity="secondary" onClick={onClose} />
                    )}
                </div>
            )}

            {error && <div className="alert alert-warning py-2 mb-2">{error}</div>}

            <TabView className={compact ? 'unified-task-details-tabs' : undefined}>
                <TabPanel header="Details">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                <Badge bg="primary">{task.status}</Badge>
                                <Badge className={getPriorityBadgeClass(task.priority)}>
                                    {task.priority}
                                </Badge>
                                {task.priority_flag === 'Priority' && (
                                    <Badge bg="dark">Priority flag</Badge>
                                )}
                                {task.missed_deadline && <Badge bg="danger">Deadline missed</Badge>}
                                {isPendingReview && <Badge bg="warning" text="dark">Pending review</Badge>}
                            </div>
                            <p className="text-muted small mb-3">{task.description || 'No description'}</p>
                            <div className="row g-2 small">
                                <div className="col-md-6">
                                    <strong>Assignee:</strong>{' '}
                                    <span className="d-inline-flex align-items-center gap-1">
                                        <Avatar
                                            image={task.assignee?.profile_image || noUserImg}
                                            shape="circle"
                                            size="normal"
                                        />
                                        {assigneeName}
                                    </span>
                                </div>
                                <div className="col-md-6">
                                    <strong>Progress:</strong> {task.progress ?? 0}%
                                </div>
                                {task.review_status && task.review_status !== 'none' && (
                                    <div className="col-md-6">
                                        <strong>Review:</strong> {task.review_status}
                                        {task.reviewer_user_id != null && (
                                            <> · {reviewerName}</>
                                        )}
                                    </div>
                                )}
                                <div className="col-md-6">
                                    <strong>Planned start:</strong> {formatDateTime(task.planned_start_at)}
                                </div>
                                <div className="col-md-6">
                                    <strong>Deadline:</strong> {formatDateTime(task.deadline_at)}
                                </div>
                                <div className="col-md-6">
                                    <strong>Estimated hours:</strong>{' '}
                                    {task.estimated_hours != null ? `${task.estimated_hours}h` : '—'}
                                </div>
                                {task.member_allocations?.length > 1 && (
                                    <div className="col-12">
                                        <strong>Hour split:</strong>{' '}
                                        {task.member_allocations
                                            .map((m) =>
                                                `${m.first_name || ''} ${m.last_name || ''}: ${
                                                    m.allocated_hours ?? '—'
                                                }h`
                                            )
                                            .join(' · ')}
                                        {!task.allocation_valid && (
                                            <Badge bg="warning" text="dark" className="ms-2">
                                                Allocation mismatch
                                            </Badge>
                                        )}
                                    </div>
                                )}
                                <div className="col-md-6">
                                    <strong>Task type:</strong> {task.task_type || '—'}
                                </div>
                                <div className="col-md-6">
                                    <strong>Updated:</strong> {formatDateTime(task.updated_at)}
                                </div>
                            </div>
                            <div className="border-top pt-3 mt-2">
                                <h6 className="small mb-2">Review</h6>
                                {isPendingReview && (
                                    <p className="small text-muted mb-2">
                                        {isCard ? 'Card is awaiting review' : (
                                            <>Task is in <strong>In Review</strong></>
                                        )}
                                        {task.review_status && task.review_status !== 'none' && (
                                            <> · Review status: <strong>{task.review_status}</strong></>
                                        )}
                                        {task.reviewer_user_id != null && (
                                            <> · Reviewer: <strong>{reviewerName}</strong></>
                                        )}
                                        {isSameUser(task.reviewer_user_id, userId) && (
                                            <span className="text-success"> (you)</span>
                                        )}
                                    </p>
                                )}
                                {canRequestReview && (
                                    <>
                                        <p className="small text-muted mb-2">
                                            {task.review_status === 'changes_requested'
                                                ? 'Address feedback, then re-submit for review (previous reviewer pre-selected).'
                                                : 'Pick a reviewer, then click Request review.'}
                                        </p>
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            <Dropdown
                                                value={selectedReviewerId}
                                                options={reviewUsers}
                                                onChange={(e) => setSelectedReviewerId(e.value)}
                                                placeholder="Select reviewer"
                                                className="flex-grow-1"
                                                style={{ minWidth: '200px' }}
                                            />
                                            <Button
                                                label={
                                                    task.review_status === 'changes_requested'
                                                        ? 'Re-submit for review'
                                                        : 'Request review'
                                                }
                                                size="small"
                                                onClick={handleRequestReview}
                                                loading={reviewSubmitting}
                                                disabled={
                                                    !selectedReviewerId &&
                                                    task.review_status !== 'changes_requested'
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                                {isAssignedReviewer && (
                                    <div className="mt-2">
                                        <p className="small text-success mb-2">
                                            You are the reviewer — approve or request changes below.
                                        </p>
                                        <InputTextarea
                                            value={reviewComment}
                                            onChange={(e) => setReviewComment(e.target.value)}
                                            rows={2}
                                            className="w-100 mb-2"
                                            placeholder="Optional feedback for assignee..."
                                        />
                                        <div className="d-flex gap-2">
                                            <Button
                                                label="Approve"
                                                severity="success"
                                                size="small"
                                                onClick={handleApproveReview}
                                                loading={reviewSubmitting}
                                            />
                                            <Button
                                                label="Request changes"
                                                severity="warning"
                                                size="small"
                                                onClick={handleRequestChanges}
                                                loading={reviewSubmitting}
                                            />
                                        </div>
                                    </div>
                                )}
                                {!canRequestReview && !isAssignedReviewer && isPendingReview && (
                                    <p className="small text-muted mb-0">
                                        Waiting for reviewer
                                        {task.reviewer_user_id != null ? ` (${reviewerName})` : ''}.
                                    </p>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </TabPanel>

                <TabPanel header={`Comments (${comments.length})`}>
                    <div className="mb-3" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        {comments.length === 0 ? (
                            <p className="text-muted small">No comments yet.</p>
                        ) : (
                            comments.map((c) => (
                                <div key={c.id} className="border-bottom pb-2 mb-2">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <Avatar
                                            image={c.author?.profile_image || noUserImg}
                                            shape="circle"
                                            size="normal"
                                        />
                                        <strong className="small">
                                            {resolveCommentAuthorName(c)}
                                        </strong>
                                        <small className="text-muted ms-auto">
                                            {formatDateTime(c.created_at)}
                                        </small>
                                    </div>
                                    <p className="mb-0 small ms-5">{c.body || c.comment_text || ''}</p>
                                </div>
                            ))
                        )}
                    </div>
                    {!isCard && (
                        <>
                            <InputTextarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                rows={3}
                                className="w-100"
                                placeholder="Write a comment..."
                            />
                            <Button
                                label="Add comment"
                                icon="pi pi-send"
                                className="mt-2"
                                onClick={handleAddComment}
                                loading={submitting}
                                disabled={!newComment.trim()}
                            />
                        </>
                    )}
                </TabPanel>

                <TabPanel header={`Attachments (${attachments.length})`}>
                    {attachments.length === 0 ? (
                        <p className="text-muted small">No attachments on this task yet.</p>
                    ) : (
                        <ul className="list-unstyled mb-0">
                            {attachments.map((a) => (
                                <li key={a.id} className="mb-2 border-bottom pb-2">
                                    <a href={a.file_url} target="_blank" rel="noopener noreferrer">
                                        {a.file_type === 'image' ? (
                                            <img
                                                src={a.file_url}
                                                alt="attachment"
                                                style={{ maxWidth: '120px', maxHeight: '80px' }}
                                                className="me-2 rounded"
                                            />
                                        ) : (
                                            <i className="pi pi-file me-2" />
                                        )}
                                        {a.file_subtype || a.file_type || 'File'}
                                    </a>
                                    <small className="text-muted d-block">
                                        {formatDateTime(a.created_at)}
                                    </small>
                                </li>
                            ))}
                        </ul>
                    )}
                    {!isCard && (
                        <div className="mt-3">
                            <input
                                type="file"
                                className="form-control form-control-sm"
                                onChange={handleUploadAttachment}
                                disabled={uploading}
                            />
                            {uploading && (
                                <small className="text-muted d-block mt-1">Uploading...</small>
                            )}
                        </div>
                    )}
                </TabPanel>

                <TabPanel header={`Activity (${activity.length})`}>
                    {activity.length === 0 ? (
                        <p className="text-muted small mb-0">No activity recorded yet.</p>
                    ) : (
                        <ul className="unified-activity-list">
                            {activity.map((item) => (
                                <li key={item.id ?? item.created_at} className="unified-activity-item">
                                    <strong className="small d-block">{item.summary}</strong>
                                    <small className="text-muted d-block">
                                        {formatDateTime(item.created_at)}
                                    </small>
                                    {item.actor && (
                                        <small className="d-flex align-items-center gap-1 mt-1">
                                            <Avatar
                                                image={item.actor.profile_image || noUserImg}
                                                shape="circle"
                                                style={{ width: '22px', height: '22px' }}
                                            />
                                            {resolveActivityActorName(item)}
                                        </small>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </TabPanel>
            </TabView>
        </div>
    );
};

export default UnifiedTaskDetails;
